// hooks/useChat.js
import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { conversation } from "network/ApiAxios";
import { useChatContext } from 'contexts/ChatContext';
import config from 'core/config';

const MIN_LOADING_TIME_MS = 800;

export const useChat = (id) => {
    const navigate = useNavigate();
    const { conversationsUpdated, refreshConversations } = useChatContext();
    const token = localStorage.getItem('token');

    // State
    const [messages, setMessages] = useState([]);
    const [userPrompt, setUserPrompt] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [regenerateMessageId, setRegenerateMessageId] = useState(null);
    const [loadingStartTime, setLoadingStartTime] = useState(null);

    const scrollRef = useRef(null);
    const formRef = useRef(null);
    const textareaRef = useRef(null);

    // Auto-resize textarea
    const adjustTextareaHeight = () => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = `${textarea.scrollHeight}px`;
            textarea.style.overflowY = textarea.scrollHeight > 200 ? 'auto' : 'hidden';
        }
    };

    // Load conversation - simple effect
    useEffect(() => {
        if (id) {
            const getConversation = async () => {
                try {
                    const data = await conversation(token, id);
                    setMessages(data.mapping ? Object.values(data.mapping) : []);
                } catch (error) {
                    console.error('Failed to fetch conversation:', error);
                    setMessages([]);
                }
            };
            getConversation();
        }
    }, [id, token, regenerateMessageId, conversationsUpdated]);

    // Auto-scroll
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    // Create user message - memoized only once
    const createUserMessage = useMemo(() => (queryText) => {
        const timestamp = new Date().getTime();
        const messageId = `user-${timestamp}`;

        return {
            id: messageId,
            message: {
                id: messageId,
                author: { role: "user" },
                create_time: new Date().toISOString(),
                update_time: new Date().toISOString(),
                content: {
                    content_type: "text",
                    parts: [queryText]
                },
            },
            parent: null,
            children: []
        };
    }, []);

    // Handle message submission - SIMPLE approach
    const handleSubmit = async (event, query = null, messageId = null, parentId = null) => {
        if (event?.preventDefault) event.preventDefault();

        const submitQuery = query ?? userPrompt.trim();
        if (!submitQuery) return;

        // Simple duplicate prevention
        if (isLoading) {
            console.log('Request in progress, ignoring');
            return;
        }

        setIsLoading(true);
        setLoadingStartTime(Date.now());
        setUserPrompt('');

        // Add user message to UI
        if (!messageId) {
            const userMessage = createUserMessage(submitQuery);
            const assistantMessage = {
                id: `assistant-${Date.now()}`,
                message: {
                    id: `assistant-${Date.now()}`,
                    author: { role: "assistant" },
                    create_time: new Date().toISOString(),
                    update_time: new Date().toISOString(),
                    content: { content_type: "text", parts: [""] }
                },
                parent: null,
                children: []
            };

            setMessages(prev => [...prev, userMessage, assistantMessage]);
        } else {
            setRegenerateMessageId(messageId);
        }

        try {
            // Custom SSE implementation that handles tab switching better
            const response = await fetch(config.API_BASE_URL + 'v1/conversation/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${token}`,
                    'Accept': 'text/event-stream',
                },
                body: JSON.stringify({
                    message: submitQuery,
                    parent_id: parentId || null,
                    session_id: id || null,
                    message_id: messageId || null
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = ''; // Buffer for incomplete messages

            while (true) {
                const { done, value } = await reader.read();

                if (done) break;

                // Add new chunk to buffer
                buffer += decoder.decode(value, { stream: true });

                // Split buffer by double newlines (SSE message separator)
                const messages = buffer.split('\n\n');

                // Keep the last (potentially incomplete) message in buffer
                buffer = messages.pop() || '';

                // Process complete messages
                for (const message of messages) {
                    if (!message.trim()) continue;

                    const lines = message.split('\n');
                    for (const line of lines) {
                        if (line.startsWith('data: ')) {
                            try {
                                const data = line.slice(6); // Remove 'data: '
                                if (data.trim() === '') continue;

                                const parsed = JSON.parse(data);

                                if (parsed.status === 'DONE') {
                                    if (parsed.conversation_id && parsed.conversation_id !== id) {
                                        navigate(`/c/${parsed.conversation_id}`);
                                    }
                                    return;
                                }

                                if (parsed.message?.content?.parts) {
                                    const content = parsed.message.content.parts[0];

                                    setMessages(prevMessages => {
                                        return prevMessages.map((msg, index) => {
                                            const isLastAssistantMessage = msg.message?.author?.role === 'assistant' &&
                                                index === prevMessages.length - 1;

                                            if (isLastAssistantMessage) {
                                                return {
                                                    ...msg,
                                                    id: parsed.id || msg.id,
                                                    message: {
                                                        ...msg.message,
                                                        id: parsed.id || msg.message.id,
                                                        content: {
                                                            ...msg.message.content,
                                                            parts: [content]
                                                        }
                                                    },
                                                    parent: parsed.parent || msg.parent
                                                };
                                            }
                                            return msg;
                                        });
                                    });
                                }
                            } catch (parseError) {
                                console.error('Error parsing SSE message:', parseError);
                                // Log the problematic data for debugging
                                console.log('Problematic data:', line.slice(6));
                            }
                        }
                    }
                }
            }

        } catch (error) {
            console.error('Failed to send message:', error);
        } finally {
            const minLoadingTime = MIN_LOADING_TIME_MS;
            const elapsedTime = loadingStartTime ? Date.now() - loadingStartTime : 0;
            const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

            setTimeout(() => {
                setIsLoading(false);
                setLoadingStartTime(null);
                setRegenerateMessageId(null);

                if (messageId) {
                    refreshConversations();
                }
            }, remainingTime);
        }
    };

    // Handle keyboard events - simple
    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey && !isLoading) {
            event.preventDefault();
            handleSubmit(event);
        }
    };

    return {
        // State
        messages,
        userPrompt,
        isLoading,
        regenerateMessageId,

        // Setters
        setUserPrompt,

        // Refs
        scrollRef,
        formRef,
        textareaRef,

        // Functions
        handleSubmit,
        handleKeyDown,
        adjustTextareaHeight
    };
};